import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import clsx from 'clsx';
import { useParams } from 'react-router-dom';

import * as actions from 'containers/ProjectTools/Notes/actions';
import APModal from 'components/Modal/APModal';
import GetLinkModal from 'components/Modal/GetLink';
import { APTooltipProvider } from 'components/Tooltip';
import { getStyleGuideList, getColorPalettes } from 'apollo/notes.query';
import NoteListForm from './NoteListForm';
import StyleGuideForm from './StyleGuideForm';
import ColorPaletteForm from './ColorPaletteForm';
import EmailEditForm from './EmailEditForm';
import EmailPreviewForm from './EmailPreviewForm';
import { getShareUrl } from './api/getShareUrl';
import './styles.scss';

const SHARE_PLUS_STEPS = {
  NOTES: 0,
  STYLE_GUIDES: 1,
  COLOR_PALETTE: 2,
  EMAIL_TEMPLATE: 3,
  EMAIL_PREVIEW: 4,
};

const SHARE_PLUS_FORM_TITLES = {
  NOTES: 'Share your notes.',
  STYLE_GUIDES: 'Share your style guides.',
  COLOR_PALETTE: 'Share your color palettes.',
  EMAIL_TEMPLATE: 'Create new email.',
  EMAIL_PREVIEW: '',
};

const SharePlusModal = ({ open, onClose, currentNote }) => {
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const notes = useSelector((state) => state.projectTools.notes?.items);
  const notesLoading = useSelector((state) => state.projectTools.notes.notesLoading);
  const noteGroups = useSelector(state => state.projectTools.notes.groups);

  // Share permissions.
  const { personas } = useSelector((state) => state.global.userInfo);
  const { isOrgOwner, isOrgMember } = personas || {};
  const isInOrg = isOrgOwner || isOrgMember;
  const { features } = useSelector((state) => state.projectTools.project);
  const designStudioFeature = useMemo(
    () => Array.isArray(features) ?
      features.find(feat => feat.id === 'design_studio_enabled') : null,
    [features],
  );
  const { features: featuresReleased } = useSelector((state) => state.projects.currentProject);
  const { designStudioReleased } = featuresReleased || {};
  const weddingPermission = useSelector((state) => state.projects.weddingPermission);
  const designStudioViewPermission = weddingPermission.find(p => p.name === 'design-studio.view-tool');
  const canViewDesignStudio = designStudioViewPermission ? designStudioViewPermission.value : false;
  const sharableDesignStudio = canViewDesignStudio && designStudioFeature?.checked &&
    (isInOrg || designStudioReleased);

  const [step, setStep] = useState(SHARE_PLUS_STEPS.NOTES);
  const [formTitle, setFormTitle] = useState(SHARE_PLUS_FORM_TITLES.NOTES);
  const [showGetLinkModal, setShowGetLinkModal] = useState(false);
  const [error, setError] = useState('');

  const [notesItems, setNotesItems] = useState(notes);
  const [styleGuides, setStyleGuides] = useState([]);
  const [colorPalettes, setColorPalettes] = useState([]);

  const [sharedNotes, setShareNotes] = useState(currentNote ? [ currentNote ] : []);
  const [sharedStyleGuides, setShareStyleGuides] = useState([]);
  const [sharedColorPalette, setShareColorPalette] = useState([]);
  const [emailFormData, setEmailFormData] = useState(null);

  const [shareUrl, setShareUrl] = useState();

  useEffect(() => {
    setNotesItems(notes);
  }, [notes]);

  useEffect(() => {
    setFormTitle(Object.values(SHARE_PLUS_FORM_TITLES)[step]);
  }, [step]);

  const {
    getNotes,
    getNoteGroups,
  } = bindActionCreators(actions, dispatch);

  const shareIsEmpty = () => {
    return sharedNotes.length === 0 && sharedStyleGuides.length === 0 && sharedColorPalette.length === 0;
  };

  const goToStep = (_step) => {
    setError('');
    if (_step === SHARE_PLUS_STEPS.EMAIL_TEMPLATE && shareIsEmpty()) {
      setError('Please go back and select an item or items to share.');
      return;
    }
    setStep(_step);
  };

  const handleClose = () => {
    // If user is previwing an email, we don't want to completely close the modal
    // which gets rid of all the work done in  share plus modal.
    if (step === SHARE_PLUS_STEPS.EMAIL_PREVIEW) {
      goToStep(SHARE_PLUS_STEPS.EMAIL_TEMPLATE);
    } else {
      onClose();
    }
  };

  const preFetchData = async () => {
    const response = await getStyleGuideList(projectId);
    setStyleGuides(response?.data?.styleGuidesList || []);
    const response1 = await getColorPalettes(projectId);
    setColorPalettes(response1?.data?.weddingPalettesList || []);
  };

  const dismiss = () => onClose();

  useEffect(() => {
    if (!noteGroups) {
      getNotes({ weddingId: projectId, notesLoading: true });
      getNoteGroups({ weddingId: projectId });
    }
    preFetchData();
  }, []);

  const fetchShareUrl = async () => {
    if (shareIsEmpty()) {
      setError('Please go back and select an item or items to share.');
      return;
    }

    const payload = {
      'style_guides': sharedStyleGuides?.map(sg => sg.id) || [],
      'palettes': sharedColorPalette?.map(cp => cp.id) || [],
      'notes': sharedNotes?.map(n => n.id) || [],
    };
    const response = await getShareUrl(payload, projectId);
    const { token, error: err } = response;
    if (err) {
      // #TODO: Add error state.
      return;
    }
    if (token) {
      const url = `${process.env.REACT_APP_AP_URL}/studio-share/${token}`;
      setShareUrl(url);
      setShowGetLinkModal(true);
    }
  };

  if (notesLoading || !noteGroups) return null;

  return (
    <>
      <APModal
        open={open}
        title={formTitle}
        name="share-note"
        className={clsx(
          'note_share_form__container',
          step === SHARE_PLUS_STEPS.EMAIL_PREVIEW && 'preview-mode',
          )}
        onClose={handleClose}
        hasHeader
        shouldCloseOnOverlayClick={false}
        errorMsg={error}
      >
        <APTooltipProvider>
          {step === SHARE_PLUS_STEPS.NOTES && (
            <NoteListForm
              items={notesItems}
              groups={noteGroups}
              shared={sharedNotes}
              addToShare={(selected) => {
                setShareNotes([...sharedNotes, notesItems?.find(item => item.id === selected)]);
              }}
              removeShare={(selected) => {
                setShareNotes([...sharedNotes?.filter(item => item.id !== selected)]);
              }}
              onNext={() => goToStep(!sharableDesignStudio ?
                SHARE_PLUS_STEPS.EMAIL_TEMPLATE : SHARE_PLUS_STEPS.STYLE_GUIDES,
              )}
              onCopyLink={fetchShareUrl}
              isLast={!sharableDesignStudio}
            />
          )}
          {step === SHARE_PLUS_STEPS.STYLE_GUIDES && (
            <StyleGuideForm
              items={styleGuides}
              shared={sharedStyleGuides}
              addToShare={(selected) => {
                setShareStyleGuides([...sharedStyleGuides, styleGuides?.find(item => item.id === selected)]);
              }}
              removeShare={(selected) => {
                setShareStyleGuides([...sharedStyleGuides?.filter(item => item.id !== selected)]);
              }}
              onPrev={() => goToStep(SHARE_PLUS_STEPS.NOTES)}
              onNext={() => goToStep(SHARE_PLUS_STEPS.COLOR_PALETTE)}
            />
          )}
          {step === SHARE_PLUS_STEPS.COLOR_PALETTE && (
            <ColorPaletteForm
              items={colorPalettes}
              shared={sharedColorPalette}
              addToShare={(selected) => {
                setShareColorPalette([...sharedColorPalette, colorPalettes?.find(item => item.id === selected)]);
              }}
              removeShare={(selected) => {
                setShareColorPalette([...sharedColorPalette?.filter(item => item.id !== selected)]);
              }}
              onCopyLink={fetchShareUrl}
              onPrev={() => goToStep(SHARE_PLUS_STEPS.STYLE_GUIDES)}
              onNext={() => goToStep(SHARE_PLUS_STEPS.EMAIL_TEMPLATE)}
              isLast={sharableDesignStudio}
            />
          )}
          {step === SHARE_PLUS_STEPS.EMAIL_TEMPLATE && (
            <EmailEditForm
              projectId={projectId}
              notes={sharedNotes}
              styleGuides={sharedStyleGuides}
              colorPalette={sharedColorPalette}
              formData={emailFormData}
              onPreview={(form) => {
                setEmailFormData(form);
                goToStep(SHARE_PLUS_STEPS.EMAIL_PREVIEW);
              }}
              dismiss={dismiss}
              onPrev={() => goToStep(!sharableDesignStudio ? SHARE_PLUS_STEPS.NOTES : SHARE_PLUS_STEPS.COLOR_PALETTE)}
              showTemplates
            />
          )}
          {step === SHARE_PLUS_STEPS.EMAIL_PREVIEW && (
            <EmailPreviewForm
              formData={emailFormData}
              onBack={() => goToStep(SHARE_PLUS_STEPS.EMAIL_TEMPLATE)}
            />
          )}
        </APTooltipProvider>
      </APModal>
      {showGetLinkModal && (
        <GetLinkModal
          open={showGetLinkModal}
          link={shareUrl}
          onClose={() => {
            setShowGetLinkModal(false);
            onClose();
          }}
        />
      )}
    </>
  );
};

SharePlusModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

SharePlusModal.defaultProps = {};

export default SharePlusModal;
